import React from 'react';
import { FileText, BarChart2, ArrowDownCircle, Hammer, Calendar, Truck } from 'lucide-react';
import { formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент сводки проекта, показывающий подробную информацию о расчетах
 */
const ProjectSummary = ({ 
  formData, 
  results, 
  projectTypes, 
  concreteGrades, 
  reinforcementTypes 
}) => {
  if (!results) return null;

  // Получаем названия выбранных параметров
  const getProjectTypeName = () => {
    const projectType = projectTypes.find(t => t.id === formData.projectType);
    return projectType ? projectType.name : formData.projectType;
  };

  const getConcreteGradeName = () => {
    const concreteGrade = concreteGrades.find(g => g.id === formData.concreteGrade);
    return concreteGrade ? concreteGrade.name : formData.concreteGrade;
  };

  const getReinforcementTypeName = () => {
    const reinforcementType = reinforcementTypes.find(r => r.id === formData.reinforcementType);
    return reinforcementType ? reinforcementType.name : formData.reinforcementType;
  };

  // Определение способа доставки
  const getDeliveryMethod = () => {
    if (!formData.includeDelivery) return 'Самовывоз';
    const trucks = results.trucksNeeded;
    return `${trucks} ${trucks === 1 ? 'машина' : trucks < 5 ? 'машины' : 'машин'} (примерно по 7м³)`;
  };

  // Определение примерной даты заливки и времени высыхания
  const getTodayPlusDays = (days) => {
    const date = new Date();
    date.setDate(date.getDate() + days);
    return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  // Расчет времени схватывания бетона (зависит от марки бетона)
  const getSettingTime = () => {
    // Базовые значения для разных марок (в днях)
    const settingTimesByGrade = {
      'M100': { initial: 1, full: 14 },
      'M150': { initial: 1, full: 16 },
      'M200': { initial: 1, full: 18 },
      'M250': { initial: 2, full: 20 },
      'M300': { initial: 2, full: 22 },
      'M350': { initial: 2, full: 24 },
      'M400': { initial: 3, full: 28 }
    };
    
    const times = settingTimesByGrade[formData.concreteGrade] || { initial: 2, full: 21 };
    
    return {
      initial: times.initial,
      full: times.full
    };
  };

  const settingTime = getSettingTime();

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mt-8">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <FileText className="w-5 h-5 mr-2 text-indigo-500" />
        Сводка проекта
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Основная информация */}
        <div className="space-y-4">
          <h4 className="text-base font-medium text-gray-800 dark:text-gray-200 mb-2">
            Основные параметры
          </h4>
          
          <div className="bg-indigo-50 dark:bg-indigo-900/20 p-4 rounded-lg border border-indigo-100 dark:border-indigo-800">
            <dl className="space-y-3 text-sm">
              <div className="flex justify-between">
                <dt className="text-gray-600 dark:text-gray-400">Тип конструкции:</dt>
                <dd className="font-medium text-gray-900 dark:text-white">{getProjectTypeName()}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-600 dark:text-gray-400">Марка бетона:</dt>
                <dd className="font-medium text-gray-900 dark:text-white">{getConcreteGradeName()}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-600 dark:text-gray-400">Тип армирования:</dt>
                <dd className="font-medium text-gray-900 dark:text-white">{getReinforcementTypeName()}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-600 dark:text-gray-400">Форма конструкции:</dt>
                <dd className="font-medium text-gray-900 dark:text-white">
                  {formData.shapeType === 'rectangle' ? 'Прямоугольная' : 
                   formData.shapeType === 'cylinder' ? 'Цилиндрическая' : 
                   formData.shapeType === 'l_shape' ? 'L-образная' : 'Произвольная'}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-600 dark:text-gray-400">Объем бетона:</dt>
                <dd className="font-medium text-gray-900 dark:text-white">
                  {results.totalVolume.toFixed(2)} м³ ({results.baseVolume.toFixed(2)} + {results.wastageVolume.toFixed(2)} запас)
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-600 dark:text-gray-400">Вес бетона:</dt>
                <dd className="font-medium text-gray-900 dark:text-white">
                  {Math.round(results.concreteWeight)} кг ({(results.concreteWeight / 1000).toFixed(1)} т)
                </dd>
              </div>
            </dl>
          </div>

          <h4 className="text-base font-medium text-gray-800 dark:text-gray-200 mb-2">
            Финансовые показатели
          </h4>
          
          <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-100 dark:border-green-800">
            <dl className="space-y-3 text-sm">
              <div className="flex justify-between">
                <dt className="text-gray-600 dark:text-gray-400">Стоимость бетона:</dt>
                <dd className="font-medium text-gray-900 dark:text-white">{formatCurrency(results.concreteCost, 0)}</dd>
              </div>
              
              {results.deliveryCost > 0 && (
                <div className="flex justify-between">
                  <dt className="text-gray-600 dark:text-gray-400">Стоимость доставки:</dt>
                  <dd className="font-medium text-gray-900 dark:text-white">{formatCurrency(results.deliveryCost, 0)}</dd>
                </div>
              )}
              
              {results.pumpCost > 0 && (
                <div className="flex justify-between">
                  <dt className="text-gray-600 dark:text-gray-400">Стоимость насоса:</dt>
                  <dd className="font-medium text-gray-900 dark:text-white">{formatCurrency(results.pumpCost, 0)}</dd>
                </div>
              )}
              
              {results.laborCost > 0 && (
                <div className="flex justify-between">
                  <dt className="text-gray-600 dark:text-gray-400">Стоимость работ:</dt>
                  <dd className="font-medium text-gray-900 dark:text-white">{formatCurrency(results.laborCost, 0)}</dd>
                </div>
              )}
              
              <div className="flex justify-between pt-2 border-t border-green-200 dark:border-green-700">
                <dt className="font-medium text-gray-700 dark:text-gray-300">Общая стоимость:</dt>
                <dd className="font-bold text-gray-900 dark:text-white">{formatCurrency(results.totalCost, 0)}</dd>
              </div>
              
              <div className="flex justify-between">
                <dt className="text-gray-600 dark:text-gray-400">Стоимость за 1 м³:</dt>
                <dd className="font-medium text-gray-900 dark:text-white">{formatCurrency(results.pricePerCubicMeter, 0)}</dd>
              </div>
            </dl>
          </div>
        </div>

        {/* Логистика и сроки */}
        <div className="space-y-4">
          <h4 className="text-base font-medium text-gray-800 dark:text-gray-200 mb-2">
            Логистика и сроки
          </h4>
          
          <div className="bg-amber-50 dark:bg-amber-900/20 p-4 rounded-lg border border-amber-100 dark:border-amber-800">
            <dl className="space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <dt className="text-gray-600 dark:text-gray-400 flex items-center">
                  <Truck className="w-4 h-4 mr-1 text-amber-600 dark:text-amber-400" /> Способ доставки:
                </dt>
                <dd className="font-medium text-gray-900 dark:text-white">{getDeliveryMethod()}</dd>
              </div>
              
              <div className="flex items-center justify-between">
                <dt className="text-gray-600 dark:text-gray-400 flex items-center">
                  <Calendar className="w-4 h-4 mr-1 text-amber-600 dark:text-amber-400" /> Время заливки:
                </dt>
                <dd className="font-medium text-gray-900 dark:text-white">
                  ~{results.totalPourTimeMinutes} мин.
                </dd>
              </div>
              
              <div className="flex items-center justify-between">
                <dt className="text-gray-600 dark:text-gray-400 flex items-center">
                  <ArrowDownCircle className="w-4 h-4 mr-1 text-amber-600 dark:text-amber-400" /> Начальное схватывание:
                </dt>
                <dd className="font-medium text-gray-900 dark:text-white">
                  {settingTime.initial} {settingTime.initial === 1 ? 'день' : 'дня'}
                  <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">
                    (прим. {getTodayPlusDays(settingTime.initial)})
                  </span>
                </dd>
              </div>
              
              <div className="flex items-center justify-between">
                <dt className="text-gray-600 dark:text-gray-400 flex items-center">
                  <Hammer className="w-4 h-4 mr-1 text-amber-600 dark:text-amber-400" /> Полное схватывание:
                </dt>
                <dd className="font-medium text-gray-900 dark:text-white">
                  {settingTime.full} дней
                  <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">
                    (прим. {getTodayPlusDays(settingTime.full)})
                  </span>
                </dd>
              </div>
            </dl>
          </div>

          <h4 className="text-base font-medium text-gray-800 dark:text-gray-200 mb-2">
            Рекомендации
          </h4>
          
          <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-100 dark:border-blue-800">
            <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
              {results.recommendations.map((recommendation, index) => (
                <li key={index} className="flex items-start">
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    className="h-4 w-4 text-blue-500 mt-0.5 mr-2 flex-shrink-0" 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={2} 
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
                    />
                  </svg>
                  <span>{recommendation}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-700/30 rounded-lg border border-gray-200 dark:border-gray-700 text-sm text-gray-600 dark:text-gray-400">
        <BarChart2 className="w-5 h-5 inline-block mr-2 text-indigo-500" />
        Расчеты основаны на средних показателях для региона {results.region}. Фактические значения могут отличаться в зависимости от поставщика, сезона и особенностей проекта.
      </div>
    </div>
  );
};

export default ProjectSummary;